import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const SERVICES = [
  {
    n: "01",
    t: "Database Management",
    d: "Schema design, performance tuning, partitioning, replication, HA/DR strategies, and end-to-end migrations.",
    bullets: ["Oracle, MS SQL, PostgreSQL", "Snowflake & cloud DW", "Zero-downtime migrations", "Cost optimization audits"],
  },
  {
    n: "02",
    t: "Data Pipeline Engineering",
    d: "Production ELT/ETL pipelines with observability, lineage, and data quality contracts.",
    bullets: ["Airflow / dbt / Fivetran", "CDC & streaming (Kafka)", "Data quality SLAs", "Lineage & cataloging"],
  },
  {
    n: "03",
    t: "Power BI Practice",
    d: "Enterprise Power BI deployments with semantic models, row-level security, and embedded analytics.",
    bullets: ["Tabular semantic models", "DAX optimization", "Workspace governance", "Embedded scenarios"],
  },
  {
    n: "04",
    t: "Tableau Practice",
    d: "Tableau Server/Cloud deployments and advanced visualization for executive audiences.",
    bullets: ["Server administration", "Performance auditing", "Action design", "Executive scorecards"],
  },
  {
    n: "05",
    t: "Qlik Practice",
    d: "Qlik Sense and QlikView consulting with associative analytics and mashup deployments.",
    bullets: ["Associative models", "Section access", "NPrinting", "Mashup extensions"],
  },
  {
    n: "06",
    t: "Sisense & MicroStrategy",
    d: "Embedded analytics with Sisense and enterprise OLAP with MicroStrategy.",
    bullets: ["Sisense Fusion", "MSTR HyperIntelligence", "Cube governance", "OEM deployments"],
  },
  {
    n: "07",
    t: "Zoho Analytics",
    d: "Mid-market BI deployments with Zoho Analytics integrated to the Zoho ecosystem and beyond.",
    bullets: ["Zoho One integrations", "Workspace design", "AI assistant tuning", "White-label portals"],
  },
  {
    n: "08",
    t: "Snowflake Architecture",
    d: "Multi-cluster warehouse design, secure data sharing, and cost governance.",
    bullets: ["Warehouse right-sizing", "Streams & tasks", "Data sharing", "FinOps controls"],
  },
  {
    n: "09",
    t: "MS SQL & Oracle",
    d: "On-prem to cloud migrations, high-availability, and legacy modernization.",
    bullets: ["Always On / RAC", "Index strategy", "PL/SQL & T-SQL tuning", "Lift-and-shift to cloud"],
  },
  {
    n: "10",
    t: "AI / ML Models",
    d: "Production machine learning and applied AI — from forecasting and classification to LLM-powered workflows.",
    bullets: ["Forecasting & demand models", "Classification & churn scoring", "LLM apps & RAG pipelines", "MLOps & monitoring"],
  },
];

const Services = () => (
  <>
    <section className="bg-[#0a0a0a] text-white grain relative overflow-hidden" data-testid="services-hero">
      <div className="absolute -top-40 right-0 w-[600px] h-[600px] bg-[#0055FF]/15 blur-[180px] rounded-full" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-20 relative">
        <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-6">Services</p>
        <h1 className="font-display text-[clamp(2.5rem,7vw,7rem)] leading-[0.9] tracking-tighter">
          Nine specialist <br />practices. <span className="text-[#0055FF]">One contract.</span>
        </h1>
        <p className="mt-10 max-w-2xl text-white/60 text-lg leading-relaxed">
          Every Pragma engagement is led by a senior architect with 12+ years
          of platform experience. We scope to deliverables, not hours.
        </p>
      </div>
    </section>

    <section className="bg-[#0a0a0a] text-white" data-testid="services-grid">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
          {SERVICES.map((s) => (
            <div
              key={s.n}
              className="p-8 lg:p-10 border-r border-b border-white/10 hover:bg-white/[0.02] transition-colors group"
              data-testid={`service-${s.n}`}
            >
              <div className="flex items-start justify-between mb-8">
                <span className="text-xs tracking-widest text-white/40">{s.n}</span>
                <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-[#0055FF] transition-colors" />
              </div>
              <h3 className="font-display text-2xl tracking-tight mb-3">{s.t}</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6">{s.d}</p>
              <ul className="space-y-2 border-t border-white/10 pt-4">
                {s.bullets.map((b) => (
                  <li key={b} className="text-xs text-white/50 flex items-center gap-2">
                    <span className="w-1 h-1 bg-[#0055FF]"></span>{b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white text-[#09090b]" data-testid="services-cta">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-12 gap-8 items-end">
        <h2 className="col-span-12 md:col-span-8 font-display text-5xl md:text-7xl tracking-tighter leading-[0.92]">
          Need something <br />off this menu?
        </h2>
        <div className="col-span-12 md:col-span-4">
          <p className="text-zinc-600 mb-6">
            We've built custom integrations for marketing attribution, supply
            chain optimization, IoT analytics, and regulatory reporting.
          </p>
          <Link to="/contact">
            <Button className="bg-black hover:bg-zinc-800 text-white rounded-none h-12 px-6 font-medium" data-testid="services-cta-button">
              Discuss a custom scope <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default Services;
